import { memo, useEffect, useRef, useState } from 'react';
import ConfettiExplosion from 'react-confetti-explosion';
import { useTranslation } from 'react-i18next';
import { findLastIndex } from 'lodash';

import { getPathBGByIndex } from '../../../../constants/pathBackground';
import { SVGIconType } from '../../../../constants/svgIcon';
import { Space } from '../../../../models/SpaceStore';
import { Profile } from '../../../../models/UserStore';
import {
  AppBar,
  JoyajoyImage,
  ProgressBar,
  StationCell,
  StationCellProps,
  StationCellType,
  SvgIcon,
} from '../../../Molecules';

import './AlbumPathListTemplate.css';
import styles from './AlbumPathListTemplate.module.css';

export type AlbumPathListTemplateProps = {
  currentUserProfile: Profile | null | undefined;
  classroom: Space | null | undefined;
  pathStyleBackground: number;
  albumName?: string;
  stations: (StationCellProps & {
    isCompleted?: boolean;
  })[];
  blinkingStationIndex?: number;
  recentStationIndex?: number;
  completedStandardGameCount: number;
  totalStandardGameCount: number;
  overallScore: string | undefined;
  totalRecordDuration: string | undefined;
  isForChallengeGameOnly: boolean;
  isGoal: boolean;
  onClickAboutJourney: () => void;
  onClickSharePath: () => void;
  onClickAlbumOptions?: () => void;
  onClickClassroom: () => void;
  onClickChallengeAll: () => void;
  onScrollToCurrent?: () => void;
  onClickPlayNextStandardGame: () => void;
  onSwitchForChallengeOnly: (choose: boolean) => void;
};

export const AlbumPathListTemplate = memo(
  ({
    currentUserProfile,
    classroom,
    pathStyleBackground,
    stations,
    albumName,
    blinkingStationIndex,
    recentStationIndex,
    completedStandardGameCount,
    totalStandardGameCount,
    overallScore,
    totalRecordDuration,
    isForChallengeGameOnly,
    isGoal,
    onClickClassroom,
    onClickAboutJourney,
    onClickSharePath,
    onClickAlbumOptions,
    // onClickChallengeAll,
    onScrollToCurrent,
    // onClickPlayNextStandardGame,
    onSwitchForChallengeOnly,
  }: AlbumPathListTemplateProps) => {
    const { t } = useTranslation();
    const spaceIconSource = classroom?.icon?.publicUrls?.small;
    const profilePictureSource =
      currentUserProfile?.profilePicture?.publicUrls?.small ?? '';

    const albumIcon = getPathBGByIndex(pathStyleBackground);
    const [showSecondConfetti, setShowSecondConfetti] = useState(false);
    const contentIcon = (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '12px',
          alignItems: 'center',
        }}
      >
        <div onClick={onClickSharePath} className={styles.journeyIcon}>
          <SvgIcon
            name={SVGIconType.Share}
            color="#333"
            width={28}
            height={28}
          />
          <p style={{ lineHeight: 'normal' }}>{t('Share')}</p>
        </div>
        <div onClick={onClickAboutJourney} className={styles.journeyIcon}>
          <SvgIcon
            name={SVGIconType.Content}
            color="#333"
            width={28}
            height={28}
          />
          <p style={{ lineHeight: 'normal' }}>{t('About')}</p>
        </div>
        {onClickAlbumOptions && (
          <div onClick={onClickAlbumOptions} className={styles.journeyIcon}>
            <SvgIcon
              name={SVGIconType.More}
              color="#333"
              width={28}
              height={28}
            />
          </div>
        )}
      </div>
    );

    const shouldBoom =
      blinkingStationIndex !== undefined &&
      stations[blinkingStationIndex]?.type === StationCellType.Goal;

    const completedItemCount =
      findLastIndex(
        [...stations].reverse(),
        (station) =>
          station.isCompleted === true || station.props.isUnlocked === true,
      ) + 1;
    // const totalItemCount = stations.length;

    // const [isShowAvatarPin, setIsShowAvatarPin] = useState(false);
    const stationCellAvatarPinRef = useRef<HTMLDivElement>(null);

    // useEffect(() => {
    //   const options = {
    //     root: null,
    //     rootMargin: '0px',
    //     threshold: 0,
    //   };

    //   const observer = new IntersectionObserver(
    //     (entries: IntersectionObserverEntry[]) => {
    //       if (entries[0]?.isIntersecting) {
    //         setIsShowAvatarPin(false);
    //       } else {
    //         setIsShowAvatarPin(true);
    //       }
    //     },
    //     options,
    //   );

    //   const currentRef = stationCellAvatarPinRef.current;
    //   if (currentRef) {
    //     observer.observe(currentRef);
    //   }

    //   return () => {
    //     if (currentRef) {
    //       observer.unobserve(currentRef);
    //     }
    //   };
    // }, []);

    const hasChallengeStation = stations.find(
      (station) => station.type === StationCellType.Challenge,
    );
    const goalStation = document.querySelector(
      `.goal-station-cell .game-component `,
    ) as HTMLElement;

    useEffect(() => {
      if (isGoal && goalStation) {
        goalStation.style.minHeight = '120px';
      }
    }, [isGoal, goalStation]);

    return (
      <div className="album-path-list">
        <div
          className="album-path-bg"
          style={{
            backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.3)),
             url(${albumIcon})`,
          }}
        />
        <AppBar
          title={albumName}
          onClickBack={onClickClassroom}
          trailing={contentIcon}
          leading={
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <div onClick={onClickClassroom}>
                <JoyajoyImage
                  resource={{ uri: spaceIconSource ?? profilePictureSource }}
                  style={{
                    width: '2rem',
                    aspectRatio: 1,
                    borderRadius: '50%',
                    border: '1px #E5E5E5 solid',
                    cursor: 'pointer',
                  }}
                />
              </div>
              <p>·</p>
            </div>
          }
          withBack={true}
        />
        <div className="centered-container">
          <div className="path-list-view">
            {stations.map((item, index) => {
              const reversedIndex =
                recentStationIndex === undefined
                  ? stations.length - index
                  : stations.length - 1 - index; // Calculate reversed index
              const isCurrent = isGoal
                ? reversedIndex === stations.length - 1
                : recentStationIndex !== undefined
                ? reversedIndex === recentStationIndex
                : reversedIndex + 1 === completedItemCount;

              // console.log(reversedIndex, stations.length - 1, isGoal);

              return (
                <div
                  key={index}
                  className={`station-wrapper ${
                    blinkingStationIndex === index ? 'blink-animation' : ''
                  }`}
                >
                  <StationCell {...item} />
                  {shouldBoom && blinkingStationIndex === index && (
                    <div
                      style={{
                        position: 'absolute',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        pointerEvents: 'none',
                      }}
                    >
                      <ConfettiExplosion
                        duration={2000}
                        force={0.4}
                        zIndex={9999} /* LOADING_SPINNER_ZINDEX */
                        colors={[
                          '#ff577f70',
                          '#ff884b',
                          '#ffd38470',
                          '#fff9b0',
                          '#3498db70',
                        ]}
                        onComplete={() => {
                          setShowSecondConfetti(true);
                        }}
                      />
                      {showSecondConfetti && (
                        <ConfettiExplosion
                          duration={2000}
                          force={0.4}
                          zIndex={9999} /* LOADING_SPINNER_ZINDEX */
                          colors={[
                            '#ff577f70',
                            '#ff884b',
                            '#ffd38470',
                            '#fff9b0',
                            '#3498db70',
                          ]}
                          onComplete={() => {
                            setShowSecondConfetti(false);
                          }}
                        />
                      )}
                    </div>
                  )}
                  {isCurrent && (
                    <div
                      className={
                        recentStationIndex === undefined
                          ? styles.firstStationCellUserIndicator
                          : styles.stationCellUserIndicator
                      }
                      ref={stationCellAvatarPinRef}
                    >
                      <SvgIcon
                        name={SVGIconType.BubbleIndicator}
                        width={52}
                        height={52}
                        color={'#3BB9AE'}
                      />
                      <div style={{ position: 'absolute', top: 4 }}>
                        <JoyajoyImage
                          resource={{ uri: profilePictureSource }}
                          style={{
                            width: '36px',
                            aspectRatio: 1,
                            borderRadius: '50%',
                            border: '1px #E5E5E5 solid',
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div
            style={{
              flexDirection: 'row',
              display: 'flex',
              width: '100%',
              padding: '1.5rem 2.4rem',
              alignItems: 'center',
            }}
          >
            <div className={styles.progressiveLearningZoneBannerLeadingIcon}>
              <SvgIcon
                name={SVGIconType.ProgressArrow}
                width={32}
                height={32}
                color={'#ffffff'}
                style={{
                  filter: 'drop-shadow(0px 0px 1px #00000050)', // Drop shadow
                  opacity: 0.85,
                }}
              />
            </div>
            <div className={styles.progressiveLearningTitle}>
              {isForChallengeGameOnly ? (
                <SvgIcon
                  name={SVGIconType.PracticeTick}
                  width={32}
                  height={32}
                  style={{
                    marginRight: '7px',
                    filter: 'drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.5))',
                  }}
                />
              ) : (
                <SvgIcon
                  name={SVGIconType.TutorGames}
                  width={32}
                  height={32}
                  style={{
                    marginRight: '7px',
                    filter: 'drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.5))',
                  }}
                />
              )}

              {t(
                isForChallengeGameOnly
                  ? 'Memory Practice Only'
                  : 'Progressive Learning',
              )}
            </div>
          </div>
          {hasChallengeStation ? (
            <div className={styles.togglePathContainer}>
              <div
                className={`${styles.iconTextContainer} ${
                  isForChallengeGameOnly
                    ? styles.nonSelectedPart
                    : styles.learningPart
                }`}
                onClick={() => {
                  onSwitchForChallengeOnly(false);
                }}
              >
                <SvgIcon name={SVGIconType.TutorGames} width={32} height={32} />
                {t('Progressive Learning')}
              </div>
              <div className={styles.verticalLine} />
              <div
                className={`${styles.iconTextContainer} ${
                  isForChallengeGameOnly
                    ? styles.practicePart
                    : styles.nonSelectedPart
                }`}
                onClick={() => {
                  onSwitchForChallengeOnly(true);
                }}
              >
                <SvgIcon
                  name={SVGIconType.PracticeTick}
                  width={32}
                  height={32}
                />
                {t('Memory Practice Only')}
              </div>
            </div>
          ) : null}
          <div style={{ height: '84px', width: '100%' }} />
          {/* space hold for progressiveLearningZoneBanner */}
          <div
            className={styles.progressiveLearningZoneBanner}
            // onClick={onClickPlayNextStandardGame}
          >
            <div className={styles.avatarLocationIndicator}>
              <div
                className={styles.avatarPin}
                onClick={(e) => {
                  e.stopPropagation();
                  onScrollToCurrent?.();
                }}
              >
                <SvgIcon
                  name={SVGIconType.BubbleIndicator}
                  width={36}
                  height={36}
                  color={'#3BB9AE'}
                />
                <div style={{ position: 'absolute', top: 2 }}>
                  <JoyajoyImage
                    resource={{ uri: profilePictureSource }}
                    style={{
                      width: '26px',
                      aspectRatio: 1,
                      borderRadius: '50%',
                      border: '1px #E5E5E5 solid',
                    }}
                  />
                </div>
              </div>
              <div className={styles.locationIndicatorPlate} />
            </div>

            <div className={styles.progressiveLearningTitleScoreContainer}>
              <div className={styles.progressiveLearningTitleContainer}>
                <SvgIcon
                  name={SVGIconType.PracticeTick}
                  width={24}
                  height={24}
                  color={'#46CBC0'}
                />
                <div className={styles.aiMemoryPracticeTitle}>
                  {t('Memory Practice Only')}
                </div>
              </div>
              {overallScore !== undefined &&
                totalRecordDuration !== undefined && (
                  <div className={styles.progressiveLearningScoreContainer}>
                    <div className={styles.recordContainer}>
                      {t('Record: ')}
                      <SvgIcon
                        name={SVGIconType.DoubleScore}
                        width={24}
                        height={24}
                      />
                      {`${overallScore}%`}
                    </div>
                    <div className={styles.recordContainer}>
                      <SvgIcon
                        name={SVGIconType.DoubleClock}
                        width={24}
                        height={24}
                      />
                      {totalRecordDuration}
                    </div>
                  </div>
                )}
            </div>

            <div className={styles.standardGameProgressContainer}>
              {totalStandardGameCount > 0 &&
              completedStandardGameCount >= totalStandardGameCount ? (
                <SvgIcon
                  name={'Complete'}
                  width={24}
                  height={24}
                  color="#42CDC2"
                />
              ) : totalStandardGameCount > 0 ? (
                <ProgressBar
                  current={completedStandardGameCount}
                  total={totalStandardGameCount}
                  width="3.2rem"
                  height="0.5rem"
                  borderRadius="100px"
                  fillColor="#34A79C"
                />
              ) : null}

              <p
                className={styles.standardGameProgressNumber}
              >{`${completedStandardGameCount}/${totalStandardGameCount}`}</p>
            </div>

            {/* <div
              className="play-button"
              style={{
                padding: '0.3rem',
                borderRadius: '4rem',
                backgroundColor: ' #ffffff15',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                border: '1px solid #ff9500',
              }}
            >
              <SvgIcon
                name={SVGIconType.Play}
                width={24}
                height={24}
                color="#ff9500"
              />
            </div> */}
          </div>
        </div>
      </div>
    );
  },
);
